import React, { useState, useCallback } from "react";
import { useDispatch } from "react-redux";

import Input from "app/components/Input";
import { CheckIcon, CrossIcon } from "app/components/Icons";
import { ticketGroupsActions } from "../../../Tickets/redux/slice";
import { TicketGroup } from "../../../Tickets/types";

interface Props {
  maxCount: number;
  group: TicketGroup;
  boardId: string;
  onClose: () => void;
  updateBoardsGroupsJson: (id: string) => void;
}

const TicketLimitEditor: React.FC<Props> = ({
  maxCount,
  group,
  boardId,
  onClose,
  updateBoardsGroupsJson,
}) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState<string>(String(maxCount ?? ""));

  const save = useCallback(() => {
    const num = Number(value || 0);

    if (num === maxCount) return onClose();

    dispatch(
      ticketGroupsActions.updateGroupActions({
        ...group,
        ticketLimit: num,
      }),
    );

    updateBoardsGroupsJson(boardId);
    onClose();
  }, [
    dispatch,
    value,
    group,
    boardId,
    updateBoardsGroupsJson,
    maxCount,
    onClose,
  ]);

  const cancel = useCallback(() => {
    onClose();
  }, [onClose]);

  /* 🔥 KEYBOARD HANDLER */

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") save();
    if (e.key === "Escape") cancel();
  };

  return (
    <div className="py-2">
      <div className="border-b border-gray-100 py-3 px-4">
        <span className="text-sm font-semibold uppercase text-secondary">
          Ticket Limit
        </span>
      </div>

      <div className="py-3 px-4 flex flex-col gap-3">
        <span className="text-xs text-secondary">
          Ticket amount will be highlighted if it exceeds the limit
        </span>

        <Input
          id="ticket-limit"
          label="Tickets limit"
          name="value"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={5}
          value={value}
          autoFocus
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, "");
            setValue(val);
          }}
          onKeyDown={handleKeyDown}
          placeholder="No limit"
          endIcon={
            value && (
              <div className="flex items-center gap-3">
                {Number(value) !== maxCount && (
                  <button onClick={save}>
                    <CheckIcon color="#5A8DF1" />
                  </button>
                )}

                <button onClick={cancel}>
                  <CrossIcon color="#79808A" />
                </button>
              </div>
            )
          }
        />
      </div>
    </div>
  );
};

export default React.memo(TicketLimitEditor);
